/**
 * TotalsPanel — Card "Totaux (aperçu)" avec sous-total HTVA + TVA par taux + TVAC.
 *
 * Phase 3c :
 * - Si ≥1 ligne en régime cocontractant : affiche une ligne dédiée "TVA — Autoliquidation".
 * - Bloc Options non incluses conservé.
 */
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrencyBE } from "@/lib/currency";

export default function TotalsPanel({ preview, accentColor }) {
  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="quote-totals">
      <CardContent className="space-y-2 p-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500">Totaux (aperçu)</h3>
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-slate-400">Sous-total HTVA</span>
          <span className="tabular text-sm font-semibold text-slate-100" data-testid="total-htva">
            {formatCurrencyBE(preview.subtotal)}
          </span>
        </div>
        {Object.entries(preview.byRate).map(([rate, amount]) => (
          <div key={rate} className="flex items-baseline justify-between">
            <span className="text-sm text-slate-400">TVA {rate}%</span>
            <span className="tabular text-sm text-slate-200" data-testid={`total-vat-${rate}`}>
              {formatCurrencyBE(amount)}
            </span>
          </div>
        ))}
        {preview.hasCocontractant && (
          <div
            className="flex items-baseline justify-between rounded border border-[#7A8450]/40 bg-[#5A6338]/15 px-2 py-1"
            data-testid="total-vat-cocontractant"
          >
            <span className="text-xs font-semibold text-[#C4D68A]">TVA — Autoliquidation</span>
            <span className="tabular text-xs text-[#C4D68A]">{formatCurrencyBE(0)}</span>
          </div>
        )}
        <div className="mt-2 flex items-baseline justify-between border-t border-slate-800 pt-2">
          <span className="text-sm font-semibold text-slate-200">Total TVAC</span>
          <span
            className="tabular text-lg font-bold"
            style={{ color: accentColor }}
            data-testid="total-tvac"
          >
            {formatCurrencyBE(preview.tvac)}
          </span>
        </div>
        {preview.hasCocontractant && (
          <p className="mt-1 text-[10px] leading-tight text-[#C4D68A]" data-testid="cocontractant-notice">
            Ce devis contient des lignes en régime cocontractant. La TVA sera autoliquidée par le client (art. 20 A.R. n°1).
            Le n° TVA du client sera exigé à l&apos;envoi.
          </p>
        )}
        {preview.optionsHt > 0 && (
          <div
            className="mt-2 rounded border border-[#D99A2B]/40 bg-[#D99A2B]/10 p-2 text-xs text-[#F0C572]"
            data-testid="total-options"
          >
            <div className="mb-0.5 font-semibold">Options (non incluses)</div>
            <div className="flex justify-between">
              <span>HTVA</span>
              <span className="tabular">{formatCurrencyBE(preview.optionsHt)}</span>
            </div>
            <div className="flex justify-between">
              <span>TVAC</span>
              <span className="tabular font-semibold">{formatCurrencyBE(preview.optionsTvac)}</span>
            </div>
          </div>
        )}
        <p className="mt-2 text-[10px] leading-relaxed text-slate-600">
          Aperçu calculé côté navigateur. Le serveur recalcule à l&apos;enregistrement (source de vérité).
        </p>
      </CardContent>
    </Card>
  );
}
